import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, updatePassword as amplifyUpdateUserPassword } from 'aws-amplify/auth';
import { get, post, del } from 'aws-amplify/api';

const apiName = 'dagroApi';

// Función para obtener las cabeceras de autenticación usando el ID Token.
// El ID Token contiene los grupos de Cognito, que usaremos para la autorización en el backend.
const getAuthHeaders = async () => {
  try {
    await getCurrentUser();
    
    const { tokens } = await fetchAuthSession();
    
    if (tokens?.idToken?.toString()) {
      return {
        'Authorization': tokens.idToken.toString()
      };
    }
    
    console.warn('No se encontró token de autenticación');
    return {};
  } catch (error) {
    console.error('Error obteniendo sesión de autenticación:', error);
    return {};
  }
};

// --- SERVICIOS DE API (Sin cambios, pero ahora con access tokens) ---

export const getItems = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: '/items',
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in getItems:', error);
    throw new Error(`Failed to fetch items: ${error.message}`);
  }
};

export const getMetadata = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: '/metadata',
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in getMetadata:', error);
    throw new Error(`Failed to fetch metadata: ${error.message}`);
  }
};

export const generateUploadUrl = async (fileType, fileName) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/generate-upload-url',
      options: {
        headers: authHeaders,
        body: { fileType, fileName }
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in generateUploadUrl:', error);
    throw new Error(`Failed to generate upload URL: ${error.message}`);
  }
};

export const uploadFileToS3 = async (uploadURL, file) => {
  try {
    const response = await fetch(uploadURL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: file,
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed with status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    throw error;
  }
};

export const getJobStatus = async (jobId) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: `/job-status/${jobId}?t=${new Date().getTime()}`,
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in getJobStatus:', error);
    throw new Error(`Failed to get job status: ${error.message}`);
  }
};

export const confirmMasterUpdate = async (jobId, decision) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/confirm-master-update',
      options: {
        headers: authHeaders,
        body: { jobId, decision }
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in confirmMasterUpdate:', error);
    throw new Error(`Failed to confirm master update: ${error.message}`);
  }
};

export const processGoodsReceipt = async (bucket, key) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/process-goods-receipt',
      options: {
        headers: authHeaders,
        body: { bucket, key }
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in processGoodsReceipt:', error);
    throw new Error(`Failed to process goods receipt: ${error.message}`);
  }
};

export const approvePrices = async (approvedItems) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/approve-prices',
      options: {
        headers: authHeaders,
        body: { approvedItems }
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in approvePrices:', error);
    throw new Error(`Failed to approve prices: ${error.message}`);
  }
};

export const getApprovedUpdates = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: '/approved-updates',
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in getApprovedUpdates:', error);
    throw new Error(`Failed to get approved updates: ${error.message}`);
  }
};

export const downloadArchivedFile = async (fileKey) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: `/download-archive?file=${encodeURIComponent(fileKey)}`,
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in downloadArchivedFile:', error);
    throw new Error(`Failed to download archived file: ${error.message}`);
  }
};

// FUNCIONES QUE REQUIEREN SCOPES ADMIN (ahora funcionarán correctamente)
export const getMargins = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: '/margins',
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in getMargins:', error);
    throw new Error(`Failed to get margins: ${error.message}`);
  }
};

export const updateMargins = async (margins) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/margins',
      options: {
        headers: authHeaders,
        body: margins
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in updateMargins:', error);
    throw new Error(`Failed to update margins: ${error.message}`);
  }
};

// --- SERVICIOS DE GESTIÓN DE USUARIOS (requieren scopes admin) ---

export const listUsers = async () => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = get({
      apiName,
      path: '/users',
      options: {
        headers: authHeaders
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();
    
    return data;
  } catch (error) {
    console.error('Error in listUsers:', error);
    throw new Error(`Failed to list users: ${error.message}`);
  }
};

export const adminCreateUser = async (email, password) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = post({
      apiName,
      path: '/users',
      options: {
        headers: authHeaders,
        body: { email, password }
      }
    });
    
    const response = await restOperation.response;
    const data = await response.body.json();

    if (response.statusCode >= 400) {
      throw new Error(data.error || 'Error al crear el usuario.');
    }
    
    return data;
  } catch (error) {
    console.error('Error in adminCreateUser:', error);
    throw error;
  }
};

export const adminDeleteUser = async (username) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const restOperation = del({
      apiName,
      path: `/users/${encodeURIComponent(username)}`,
      options: {
        headers: authHeaders
      }
    });
    
    await restOperation.response;
    return { message: 'Usuario eliminado con éxito' };
  } catch (error) {
    console.error('Error in adminDeleteUser:', error);
    throw new Error(`Failed to delete user: ${error.message}`);
  }
};

export const updateUserPassword = async (oldPassword, newPassword) => {
  try {
    await amplifyUpdateUserPassword({ oldPassword, newPassword });
    return { message: 'Contraseña actualizada con éxito.' };
  } catch (error) {
    console.error('Error in updateUserPassword:', error);
    // Lanzar el error para que el componente lo maneje y muestre el feedback
    throw error;
  }
};